'use client'

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Badge from '@/components/ui/badge'
import Button from '@/components/ui/button'
import { getMeetups, getMeetupsByHostId } from '@/services/meetups'
import { formatDate, formatDateTime } from '@/utils/date-format'
import { useUser } from '@/hooks/use-user'

interface MeetupListProps {
  filter?: 'hosting' | 'attending' | 'all'
  limit?: number
}

function MeetupList({ filter = 'all', limit }: MeetupListProps) {
  const navigate = useNavigate()
  const { user, isLoading: isUserLoading } = useUser()
  const [meetups, setMeetups] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    async function loadMeetups() {
      // Don't attempt to load meetups if user is still loading
      if (isUserLoading) {
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        console.log('Loading meetups with filter:', filter, 'user:', user?.id)
        let result

        if (filter === 'hosting' && user) {
          result = await getMeetupsByHostId(user.id, limit)
        } else {
          // For 'all' or 'attending' filter
          result = await getMeetups({ limit })
        }

        if (result.error) {
          throw new Error(result.error)
        }

        // Filter meetups if needed
        let filteredMeetups = result.data || []

        if (filter === 'attending' && user) {
          // This would need a proper implementation based on your data structure
          // For now, just showing a placeholder implementation
          filteredMeetups = filteredMeetups.filter(meetup =>
            meetup.attendees?.some((attendee: any) => attendee.user_id === user.id)
          )
        }

        setMeetups(filteredMeetups)
        console.log('Successfully loaded', filteredMeetups.length, 'meetups')
      } catch (err) {
        console.error('Error loading meetups:', err)
        setError('Failed to load meetups. Please try again.')
      } finally {
        setIsLoading(false)
      }
    }

    loadMeetups()
  }, [filter, limit, user, isUserLoading, retryCount])

  const handleRetry = () => {
    setRetryCount(prev => prev + 1)
  }

  const handleMeetupClick = (id: string) => {
    navigate(`/meetups/${id}`)
  }

  // Show loading state while user data is being fetched
  if (isUserLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="mt-4 text-foreground">Loading user data...</p>
      </div>
    )
  }

  // Show loading state while meetups are being fetched
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="mt-4 text-foreground">Loading meetups...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div
        className="bg-red-900/20 border border-red-800 text-red-100 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
        <button
          onClick={handleRetry}
          className="mt-3 px-4 py-2 bg-primary text-white rounded hover:bg-primary/80"
        >
          Try Again
        </button>
      </div>
    )
  }

  if (meetups.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400 mb-4">
          {filter === 'hosting'
            ? "You're not hosting any meetups yet."
            : filter === 'attending'
            ? "You're not attending any meetups yet."
            : 'No meetups found.'}
        </p>
        <Button onClick={() => navigate('/meetups/create')}>Create a Meetup</Button>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {meetups.map(meetup => (
        <div key={meetup.id} className="block" onClick={() => handleMeetupClick(meetup.id)}>
          <div className="bg-background border border-gray-800 rounded-lg overflow-hidden hover:border-primary/50 transition-colors h-full flex flex-col">
            {meetup.cover_image_url ? (
              <div className="aspect-video bg-gray-900 relative">
                <img
                  src={meetup.cover_image_url}
                  alt={meetup.title}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="aspect-video bg-gray-900 flex items-center justify-center">
                <div className="text-gray-600">No image</div>
              </div>
            )}

            <div className="p-4 flex-1 flex flex-col">
              <div className="flex flex-wrap gap-2 mb-2">
                {meetup.status === 'draft' && <Badge variant="outline">Draft</Badge>}
                {meetup.is_private && <Badge variant="secondary">Private</Badge>}
              </div>

              <h3 className="text-xl font-bold mb-2 text-white">{meetup.title}</h3>

              {meetup.description && (
                <div className="mb-2">
                  <p className="text-gray-300 text-sm border-l-2 border-primary pl-2 line-clamp-2">
                    {meetup.description}
                  </p>
                </div>
              )}

              <div className="text-sm text-gray-400 mb-4 flex-1">
                <div className="flex items-center gap-1 mb-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{formatDateTime(meetup.start_time)}</span>
                </div>

                {meetup.location && (
                  <div className="flex items-center gap-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>{meetup.location}</span>
                  </div>
                )}
              </div>

              {meetup.host && (
                <div className="flex items-center mt-auto pt-2 border-t border-gray-800">
                  {meetup.host.avatar_url ? (
                    <img
                      src={meetup.host.avatar_url}
                      alt={meetup.host.username || meetup.host.full_name || 'Host'}
                      width={24}
                      height={24}
                      className="rounded-full mr-2"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center mr-2">
                      <span className="text-white text-xs font-bold">
                        {(meetup.host.username || meetup.host.full_name || 'H')
                          .charAt(0)
                          .toUpperCase()}
                      </span>
                    </div>
                  )}
                  <span className="text-sm text-gray-400">
                    Hosted by{' '}
                    <span className="text-white">
                      {meetup.host.username || meetup.host.full_name || 'Anonymous'}
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export default MeetupList
